import React, { useCallback, useEffect, useImperativeHandle, useMemo, useState, forwardRef } from "react";
import { useTranslation } from 'react-i18next';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView } from "react-native";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import { ProductFieldInfoVisibility } from "../../../../../view-models/ProductFieldInfoVisibility";
import Colors from "../../../../../constants/colors";
import { ComponentStyles } from "../../../../../constants/componentStyles";
import FloatingTextInputComponent from "../../../common/FloatingTextInputComponent";
import DownArrowIcon from "../../../../../../../../assets/images/DownArrowIcon.svg";
import PersonIcon from "../../../../../../../../assets/images/user-round.svg";

interface AdditionalInformationStepProps {
  onValidationChange?: (valid: boolean) => void;
}

export interface AdditionalInformationStepHandle {
  submit: () => boolean;
}

function buildSendAdditionalFieldErrors(
  requiredFields: any[],
  additionalFieldValues: Record<string, any> | null | undefined,
): Record<string, string> {
  const errs: Record<string, string> = {};
  for (const f of requiredFields) {
    const key = String(f?.XmlTag ?? f?.xmlTag ?? "");
    const label = String(f?.FieldLabel ?? f?.fieldLabel ?? key);
    const min = Number(f?.FieldMin ?? f?.fieldMin ?? 0);
    const max = Number(f?.FieldMax ?? f?.fieldMax ?? Number.MAX_SAFE_INTEGER);
    const value = String(additionalFieldValues?.[key] ?? "");
    if (!value) errs[key] = `${label} is required`;
    else if (value.length < min) errs[key] = `${label} must be at least ${min} characters`;
    else if (value.length > max) errs[key] = `${label} must be at most ${max} characters`;
  }
  return errs;
}

function isOccupationField(label: string): boolean {
  return (label ?? "").toLowerCase().includes("occupation");
}

/** Unwrap cc:enumeratedValueInfo / nested shapes from getFieldsForProduct. */
function parseEnumeratedRow(opt: any): { value: string; label: string } | null {
  const row = opt?.enumeratedValueInfo ?? opt?.EnumeratedValueInfo ?? opt;
  if (!row || typeof row !== "object") return null;
  const value = String(row?.Value ?? row?.value ?? "").trim();
  if (!value) return null;
  const labelRaw = row?.Label ?? row?.label;
  const label =
    labelRaw != null && String(labelRaw).trim() !== ""
      ? String(labelRaw).trim()
      : value;
  return { value, label };
}

/** Options for dropdown: label shown to user, value sent on send validation (additionalFieldValues / FieldValues). */
function getEnumeratedOptions(field: any): Array<{ value: string; label: string }> {
  let raw = field?.enumeratedValues ?? field?.EnumeratedValues;
  if (raw == null) return [];
  if (!Array.isArray(raw)) raw = [raw];
  const out: Array<{ value: string; label: string }> = [];
  for (const item of raw) {
    const parsed = parseEnumeratedRow(item);
    if (parsed) out.push(parsed);
  }
  return out;
}

/** Shared with MainSendCashScreen handleNext so Continue uses live store values, not stale isStepValid. */
export function areSendAdditionalFieldsValid(
  missingRequiredFields: any[] | null | undefined,
  additionalFieldValues: Record<string, any> | null | undefined,
): boolean {
  const requiredFields = (missingRequiredFields || []).filter(
    (f: any) => f?.visibility === ProductFieldInfoVisibility.REQ,
  );
  for (const f of requiredFields) {
    const key = String(f?.XmlTag ?? f?.xmlTag ?? "");
    const min = Number(f?.FieldMin ?? f?.fieldMin ?? 0);
    const max = Number(f?.FieldMax ?? f?.fieldMax ?? Number.MAX_SAFE_INTEGER);
    const value = String(additionalFieldValues?.[key] ?? "");
    if (value.length < min || value.length > max || !value) return false;
  }
  return true;
}

const AdditionalInformationStep = forwardRef<
  AdditionalInformationStepHandle,
  AdditionalInformationStepProps
>(({ onValidationChange }, ref) => {
  const { t } = useTranslation();
  const missingRequiredFields = useSessionStore((s: SessionState) => (s as any).missingRequiredFields) as any[];
  const setAdditionalFieldValue = useSessionStore((s: SessionState) => (s as any).setAdditionalFieldValue);
  const additionalFieldValues = useSessionStore((s: SessionState) => (s as any).additionalFieldValues) as Record<string, any>;

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [showFieldErrors, setShowFieldErrors] = useState(false);
  const [openDropdownKey, setOpenDropdownKey] = useState<string | null>(null);

  const requiredFields = useMemo(() => (missingRequiredFields || []).filter((f: any) => (f?.visibility === ProductFieldInfoVisibility.REQ)), [missingRequiredFields]);

  const computeFieldErrors = useCallback(
    () => buildSendAdditionalFieldErrors(requiredFields, additionalFieldValues),
    [requiredFields, additionalFieldValues],
  );

  useEffect(() => {
    onValidationChange?.(
      areSendAdditionalFieldsValid(missingRequiredFields, additionalFieldValues),
    );
  }, [requiredFields, additionalFieldValues, missingRequiredFields, onValidationChange]);

  useEffect(() => {
    if (!showFieldErrors) return;
    setFieldErrors(computeFieldErrors());
  }, [showFieldErrors, computeFieldErrors]);

  useImperativeHandle(ref, () => ({
    submit: () => {
      const errs = computeFieldErrors();
      setFieldErrors(errs);
      setShowFieldErrors(true);
      return areSendAdditionalFieldsValid(missingRequiredFields, additionalFieldValues);
    },
  }));

  const handleChange = (key: string, text: string) => {
    setAdditionalFieldValue?.(key, text);
  };

  // if (!requiredFields?.length) {
  //   return (
  //     <View style={styles.container}>
  //       <Text>No additional information required.</Text>
  //     </View>
  //   );
  // }

  return (
    <View style={styles.container}>
      <Text style={[styles.title, { paddingRight: 100 }]}>{t("WE_NEED_FEW_MORE_DETAILS")}</Text>
      {requiredFields.map((f: any, index: number) => {
        const key = String(f?.XmlTag ?? f?.xmlTag ?? "");
        const label = String(f?.FieldLabel ?? f?.fieldLabel ?? key);
        const placeholder = label;
        const value = String((additionalFieldValues?.[key] ?? ""));
        const error = showFieldErrors ? fieldErrors[key] : undefined;
        const options = getEnumeratedOptions(f);
        const useEnumeratedDropdown = options.length > 0;

        if (useEnumeratedDropdown) {
          const selectedOption = options.find((o) => o.value === value);
          // Match FloatingTextInput fields: show fieldLabel above; inside box show placeholder when empty, selected option label when chosen.
          const displayText = selectedOption ? selectedOption.label : placeholder;
          const isOpen = openDropdownKey === key;
          return (
            <View key={key} style={styles.fieldWrapper}>
              <Text style={styles.label}>{label}</Text>
              <View style={styles.dropdownCard}>
                <TouchableOpacity
                  testID={`sendAdditionalField_${key}_dropdown`}
                  style={styles.dropdownCardHeader}
                  onPress={() => setOpenDropdownKey(isOpen ? null : key)}
                  activeOpacity={0.7}
                >
                  <Text style={[styles.dropdownCardHeaderText, !value && styles.dropdownPlaceholder]} numberOfLines={1}>
                    {displayText}
                  </Text>
                  <View style={styles.dropdownArrowWrap}>
                    <DownArrowIcon width={16} height={16} />
                  </View>
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.dropdownCardListWrap}>
                    <ScrollView style={styles.dropdownCardScroll} nestedScrollEnabled>
                      {options.map((opt) => (
                        <TouchableOpacity
                          testID={`sendAdditionalField_${key}_option`}
                          key={opt.value}
                          style={styles.dropdownItem}
                          onPress={() => {
                            setAdditionalFieldValue?.(key, opt.value);
                            setOpenDropdownKey(null);
                          }}
                        >
                          <Text style={styles.dropdownItemText}>{opt.label}</Text>
                        </TouchableOpacity>
                      ))}
                    </ScrollView>
                  </View>
                )}
              </View>
              {!!error && <Text style={styles.error}>{error}</Text>}
            </View>
          );
        }

        return (
          <View key={key} style={styles.fieldWrapper}>
            <Text style={styles.label}>{label}</Text>
            <FloatingTextInputComponent
              testID={`sendAdditionalField_${key}`}
              label={placeholder}
              value={value}
              onChangeText={(t) => handleChange(key, t)}
              isAutoFocus={false}
            />
            {!!error && <Text style={styles.error}>{error}</Text>}
          </View>
        );
      })}
      {requiredFields.some((f: any) => isOccupationField(String(f?.FieldLabel ?? f?.fieldLabel ?? ""))) && (
        <View style={styles.infoBox}>
          <View style={styles.infoBoxIconWrap}>
            <PersonIcon width={24} height={24} />
          </View>
          <View style={styles.infoBoxContent}>
            <Text style={styles.infoBoxTitle}>Why do we ask these questions?</Text>
            <Text style={styles.infoBoxBody}>
              Regulations require us to verify identity before you send money. This helps prevent identity theft and keep information safe.
            </Text>
          </View>
        </View>
      )}
    </View>
  );
});

AdditionalInformationStep.displayName = "AdditionalInformationStep";

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.white,
    paddingHorizontal: 24,
  },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: '500', 
    color: Colors.titleTextColor,
    lineHeight: 34,
    marginBottom: 8
  },
  subtitle: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: '400',
    lineHeight: 24,
    color: Colors.subTitleColor,
    marginBottom: 32
  },
  fieldWrapper: {
    marginBottom: 16
  },
  dropdownCard: {
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 8,
    backgroundColor: "#FFFFFF",
    overflow: "hidden",
  },
  dropdownCardHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 12,
    paddingHorizontal: 12,
    backgroundColor: Colors.dropDownBackground
  },
  dropdownCardHeaderText: {
    fontSize: 14,
    color: "#111",
    flex: 1,
  },
  dropdownPlaceholder: {
    color: "#6B7280",
  },
  dropdownArrowWrap: {
    marginLeft: 8,
  },
  dropdownCardListWrap: {
    maxHeight: 240,
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
    backgroundColor: Colors.dropDownBackground
  },
  dropdownCardScroll: {
    maxHeight: 240,
    backgroundColor: Colors.dropDownBackground
  },
  dropdownItem: {
    paddingVertical: 12,
    paddingHorizontal: 12,
  },
  dropdownItemText: {
    fontSize: 14,
    color: "#6B7280",
  },
  infoBox: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 16,
    padding: 12,
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#1F1F1F",
    borderRadius: 8,
  },
  infoBoxIconWrap: {
    paddingRight: 16,
    paddingLeft: 4
  },
  infoBoxContent: {
    flex: 1,
  },
  infoBoxTitle: {
    fontSize: 14,
    fontWeight: "700",
    color: "#111",
    marginBottom: 4,
  },
  infoBoxBody: {
    fontSize: 14,
    fontWeight: "400",
    color: "#374151",
    lineHeight: 20,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: "700",
    marginTop: 8,
    marginBottom: 16,
    color: "#111"
  },
  label: {
    fontSize: 14,
    color: "#111",
    marginBottom: 6
  },
  input: {
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16,
    backgroundColor: "#fff"
  },
  inputError: {
    borderColor: "#E11D48"
  },
  error: {
    color: "#E11D48",
    marginTop: 6,
    fontSize: 12
  }
});

export default AdditionalInformationStep;
